import {
  ComponentProps,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Drawer } from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";

export function PaymentDrawer({
  children,
  isOpen = false,
  onClose,
  onAfterClose,
  ...props
}: ComponentProps<typeof Drawer> & {
  onAfterClose?: () => void;
}): ReactElement | null {
  const { t } = useTranslation("paymentMethods");
  const [retained, setRetained] = useState(isOpen);
  const [visible, setVisible] = useState(false);
  const mounted = isOpen || retained;
  const afterClose = useRef(onAfterClose);
  afterClose.current = onAfterClose;
  useEffect(() => {
    if (isOpen) {
      setRetained(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    const duration = window.matchMedia?.("(prefers-reduced-motion: reduce)")
      .matches
      ? 0
      : 200;
    const timer = window.setTimeout(() => {
      setRetained(false);
      afterClose.current?.();
    }, duration);
    return () => window.clearTimeout(timer);
  }, [isOpen]);
  const container = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!mounted || container.current == null) return undefined;
    const { current: root } = container;
    const { activeElement: previousFocus } = document;
    const { overflow } = document.body.style;
    const siblings = Array.from(document.body.children).filter(
      (node): node is HTMLElement =>
        node instanceof HTMLElement &&
        !node.contains(root) &&
        !node.nextElementSibling?.contains(root)
    );
    const inertValues = siblings.map((node) => node.hasAttribute("inert"));
    siblings.forEach((node) => {
      node.setAttribute("inert", "");
    });
    document.body.style.overflow = "hidden";
    root.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      // The design-system confirmation dialog owns focus while it is open.
      if (document.querySelector('[role="alertdialog"]') != null) return;
      if (event.key === "Escape") {
        if (
          root.querySelector('[role="combobox"][aria-expanded="true"]') != null
        )
          return;
        event.preventDefault();
        close.current?.();
      }
      if (event.key !== "Tab") return;
      const elements = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), a[href], iframe, [tabindex="0"]'
        )
      ).filter((node) => node.getClientRects().length > 0);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (first == null) {
        event.preventDefault();
        root.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === root)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === root)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      siblings.forEach((node, index) => {
        node.toggleAttribute("inert", inertValues[index]);
      });
      document.body.style.overflow = overflow;
      if (
        previousFocus instanceof HTMLElement &&
        previousFocus !== document.body &&
        previousFocus !== document.documentElement &&
        previousFocus.isConnected
      )
        previousFocus.focus();
      else
        document
          .querySelector<HTMLElement>("[data-payment-methods-focus]")
          ?.focus();
    };
  }, [mounted]);
  if (!mounted) return null;
  return (
    <Drawer
      {...props}
      isOpen={visible}
      mountChildrenOnOpen={false}
      onClose={isOpen ? onClose : undefined}
      width="var(--payment-drawer-width, 600px)"
      portal
      className={`payment-drawer ${
        visible ? "payment-drawer-open" : "payment-drawer-closing"
      }`}
    >
      <div
        ref={container}
        role="dialog"
        aria-modal="true"
        aria-label={t("Payment method details")}
        tabIndex={-1}
        className="payment-dialog"
      >
        {children}
      </div>
    </Drawer>
  );
}
